import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { PERMISSION_GROUPS } from "@/lib/permissions";
import type { Permission } from "@/lib/permissions";
import { useTeamMembers, type TeamMember } from "@/hooks/use-team-members";

interface Props {
  member: TeamMember;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MemberPermissionsDialog({ member, open, onOpenChange }: Props) {
  const { updatePermissions } = useTeamMembers();
  const [granted, setGranted] = useState<Set<Permission>>(new Set());

  useEffect(() => {
    setGranted(new Set((member.custom_permissions ?? []) as Permission[]));
  }, [member]);

  const toggle = (perm: Permission) => {
    setGranted(prev => {
      const next = new Set(prev);
      if (next.has(perm)) {
        next.delete(perm);
      } else {
        next.add(perm);
      }
      return next;
    });
  };

  const toggleView = (viewKey: Permission, writeKeys: Permission[]) => {
    setGranted(prev => {
      const next = new Set(prev);
      if (next.has(viewKey)) {
        // Turning view OFF → also remove all write permissions for this section
        next.delete(viewKey);
        writeKeys.forEach(k => next.delete(k));
      } else {
        next.add(viewKey);
      }
      return next;
    });
  };

  const handleSave = () => {
    updatePermissions.mutate(
      { memberId: member.id, permissions: [...granted] },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Permissions — {member.full_name ?? member.email ?? "Member"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-1 max-h-[60vh] overflow-y-auto pr-1">
          {PERMISSION_GROUPS.map(group => {
            const viewOn = granted.has(group.viewKey);
            const writeKeys = group.writePermissions.map(p => p.key);
            return (
              <div key={group.label}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5">
                  {group.label}
                </p>
                <div className="space-y-2.5">
                  {/* View toggle */}
                  <div className="flex items-center gap-2.5">
                    <Checkbox
                      id={`perm-${group.viewKey}`}
                      checked={viewOn}
                      onCheckedChange={() => toggleView(group.viewKey, writeKeys)}
                    />
                    <Label
                      htmlFor={`perm-${group.viewKey}`}
                      className="cursor-pointer font-normal text-sm"
                    >
                      View {group.label.toLowerCase()}
                    </Label>
                  </div>

                  {/* Write permissions — only shown when view is on */}
                  {viewOn && group.writePermissions.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2.5 pl-5">
                      <Checkbox
                        id={`perm-${key}`}
                        checked={granted.has(key)}
                        onCheckedChange={() => toggle(key)}
                      />
                      <Label htmlFor={`perm-${key}`} className="cursor-pointer font-normal text-sm">
                        {label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updatePermissions.isPending}>
            {updatePermissions.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
